import { sortBy } from "lodash-es";
import { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";

export interface InterleavedTranscriptItem {
  disconnected: boolean;
  isFinal: boolean;
  side: number; // 0 = mic (Me), 1 = system (Other Speakers)
  sortKey: string;
  timestamp: number;
  transcript: string;
  turn_order: number;
  formattedText: string;
  isLastOfBlock?: boolean;
}

export interface TranscriptPartLike {
  // Matches TranscriptionManager's `allTranscriptedParts` shape: an array of arrays of messages
  // interspersed with small objects that mark temporary disconnections.
  ended?: boolean;
}

export interface TranscriptChannelParts {
  channel: number; // 0 or 1
  parts: (TimestampedAssemblyAITurnMessage[] | TranscriptPartLike)[];
}

const sortKeyFor = (start: number, channel: number, disconnected = false) => {
  const channelSortOrder = channel === 0 ? 10 : channel; // mic after system
  const disconnectedMultiplier = disconnected ? 10 : 1; // disconnects after transcriptions
  return `${start.toString().padStart(16, "0")},${disconnectedMultiplier * channelSortOrder}`;
};

export function interleaveTranscriptInfo(
  channels: TranscriptChannelParts[]
): InterleavedTranscriptItem[] {
  const formatText = (text: string, side: number) =>
    `[${side === 0 ? "Me" : "Other Speakers"}]: ${text}`;

  const items = sortBy(
    channels.flatMap(manager =>
      manager.parts
        .map((part, index, parts) => {
          if (Array.isArray(part)) {
            return part
              .filter(message => message.transcript.length > 0)
              .map(({ transcript, end_of_turn, timestamp, turn_order }) => ({
                disconnected: false,
                formattedText: formatText(transcript, manager.channel),
                isFinal: end_of_turn,
                side: manager.channel,
                sortKey: sortKeyFor(timestamp, manager.channel),
                timestamp,
                transcript,
                turn_order,
              }));
          }

          const previousPart = parts[index - 1] as unknown as
            | TimestampedAssemblyAITurnMessage[]
            | undefined;
          const previousMessage = previousPart?.[previousPart.length - 1] as unknown as
            | TimestampedAssemblyAITurnMessage
            | undefined;
          const nextPart = parts[index + 1] as unknown as
            | TimestampedAssemblyAITurnMessage[]
            | undefined;
          const nextMessage = nextPart?.[0] as unknown as
            | TimestampedAssemblyAITurnMessage
            | undefined;
          const timestamp = previousMessage?.timestamp ?? nextMessage?.timestamp ?? 0;
          const turn_order =
            previousMessage?.turn_order ??
            (typeof nextMessage?.turn_order === "number" ? nextMessage.turn_order - 1 : 0);
          const transcript = (part as TranscriptPartLike).ended
            ? " ...[transcription ended]"
            : " ...[temporarily disconnected]... ";
          return [
            {
              disconnected: true,
              formattedText: formatText(transcript, manager.channel),
              isFinal: (part as TranscriptPartLike).ended ?? false,
              side: manager.channel,
              sortKey: sortKeyFor(timestamp, manager.channel, true),
              timestamp,
              transcript,
              turn_order,
            },
          ];
        })
        .flat()
    ),
    "sortKey"
  ) as unknown as InterleavedTranscriptItem[];

  // Add isLastOfBlock markers for UI convenience
  for (let i = 0; i < items.length; i++) {
    items[i].isLastOfBlock = items[i].side !== items[i + 1]?.side;
  }
  return items;
}

export function transcriptString(items: InterleavedTranscriptItem[]): string {
  return items
    .map(({ side, transcript }) => `[${side === 0 ? "Me" : "Other Speakers"}]: ${transcript}`)
    .join("\n");
}

export function transcriptArray(items: InterleavedTranscriptItem[]) {
  return items.map(({ sortKey: sort_key, transcript, side: channel, disconnected }) => ({
    sort_key,
    transcript,
    channel,
    disconnected,
  }));
}
